import * as React from 'react';
import { forwardRef, ReactElement, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { BrandColors } from 'Common/common-ui/common/theme';
import { UpdateTagType } from 'Common/enums';
import { updateTagColor } from 'Common/util';
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import Paper from '@mui/material/Paper';
import { CopyButton } from '../../CopyButton';

function createOptions(ref): HTMLReactParserOptions {
  return {
    replace: (domNode) => {
      return ReleaseNoteStyle(domNode, ref);
    },
  };
}

function ReleaseNoteStyle(domNode, ref): JSX.Element {
  const nodeName = domNode.name;
  if (
    nodeName === 'div' &&
    domNode?.attribs?.class === 'content' &&
    domNode.childNodes[1]?.name === 'pre' &&
    domNode.childNodes[1]?.attribs?.class === 'rouge highlight' &&
    domNode.childNodes[1]?.childNodes[0].name === 'code'
  ) {
    return <CopyButton domNode={domNode} />;
  }
  if (nodeName === 'div' && domNode?.attribs?.class === 'sect1') {
    return (
      <Paper elevation={3} sx={{ p: 2, pl: 3, pt: 1, mb: 2 }}>
        {domToReact(domNode.children, createOptions(ref))}
      </Paper>
    );
  }
  if (nodeName === 'h2' && domNode?.children[0]?.attribs?.class === 'anchor') {
    return (
      <Typography
        id={domNode.attribs.id}
        variant={'h3'}
        fontWeight="500"
        ref={(htmlRef: HTMLHeadingElement) => {
          ref[domNode.attribs.id] = htmlRef;
        }}
        noWrap
        sx={{
          '& hr': {
            m: 1,
            mx: -2,
          },
        }}
      >
        {domToReact(domNode.children, createOptions(ref))}
      </Typography>
    );
  }
  if (
    (nodeName === 'h3' || nodeName === 'h4') &&
    domNode?.children[0]?.attribs?.class === 'anchor' &&
    Object.values(UpdateTagType).includes(domNode?.children[1]?.data)
  ) {
    return (
      <Chip
        sx={{
          bgcolor: updateTagColor[domNode?.children[1]?.data],
          color:
            domNode?.children[1]?.data === UpdateTagType.ANNOUNCEMENTS ||
            domNode?.children[1]?.data === UpdateTagType.CHANGES
              ? BrandColors.DarkGray
              : 'background.paper',
          fontSize: 16,
          mb: 0.5,
        }}
        label={domNode?.children[1]?.data}
      />
    );
  }
  if (nodeName === 'details' && domNode?.parent?.attribs?.class === 'sect4') {
    return (
      <Box sx={{ ml: 2, mt: -2 }}>
        <details style={{ marginTop: 5, marginBottom: 8 }}>
          {domToReact(domNode.children, createOptions(ref))}
        </details>
      </Box>
    );
  }
  if (nodeName === 'summary' && domNode?.parent?.name === 'details') {
    return (
      <summary style={{ fontSize: 16 }}>
        {domToReact(domNode.children, createOptions(ref))}
      </summary>
    );
  }
  if (
    nodeName === 'h1' ||
    nodeName === 'h2' ||
    nodeName === 'h3' ||
    nodeName === 'h4' ||
    nodeName === 'h5' ||
    nodeName === 'h6'
  ) {
    return (
      <Typography
        id={domNode.attribs.id}
        variant={nodeName}
        color={BrandColors.Primary}
        fontWeight="500"
        ref={(htmlRef: HTMLHeadingElement) => {
          ref[domNode.attribs.id] = htmlRef;
        }}
        noWrap
        sx={{
          my: '8px !important',
          mb: domNode.name === 'h5' ? 3 : 1,
        }}
      >
        {domToReact(domNode.children)}
      </Typography>
    );
  }
  if (nodeName === 'div' && domNode?.attribs?.id === 'toctitle') {
    return (
      <Typography
        variant="h6"
        color={BrandColors.Primary}
        sx={{
          position: 'fixed',
          height: 65,
          bgcolor: BrandColors.White,
          pt: 2,
          mt: -2,
          width: 280,
        }}
      >
        {domToReact(domNode.children)}
        <Divider />
      </Typography>
    );
  }
  if (nodeName === 'ul' && domNode?.attribs?.class === 'sectlevel1') {
    return (
      <Box
        sx={{
          mt: 6,
          '& li': {
            p: 0.5,
            listStyleType: 'none',
            '&:hover': { textDecoration: 'underline' },
            '&:focus': { textDecoration: 'underline' },
            color: BrandColors.Teal,
          },
        }}
        className="sectlevel1"
      >
        {domToReact(domNode.children)}
      </Box>
    );
  }
}

type Props = {
  text: string;
  openImageInDialog?: (
    link: { src: string; alt: string },
    bool: boolean
  ) => void;
  ref: any;
};

function ParseAsciidoc(props: Props, ref): ReactNode {
  return parse(props.text, createOptions(ref)) as ReactElement;
}

export const ParseAsciidocWithRef = forwardRef(ParseAsciidoc);
